import { useEffect, type ReactNode } from "react";
import { ThemeProvider as NextThemes, useTheme } from "next-themes";
import { SegmentedControl } from "@/components/form";

/**
 * Light or dark (decided with Tiago 16 October): the device's own unless
 * somebody chooses on Account, and the choice is this device's — kept in the
 * browser, never sent. index.html applies it before the first paint, reading
 * the same key, so a dark page never flashes white; this keeps it after.
 */
export const THEME_STORAGE_KEY = "theme";

/** The page's background in each theme, for the browser's own bars. */
const BARS = { light: "#ffffff", dark: "#0a0a0a" } as const;

export function ThemeProvider({ children }: { children: ReactNode }) {
  return (
    <NextThemes attribute="class" defaultTheme="system" enableSystem storageKey={THEME_STORAGE_KEY} disableTransitionOnChange>
      <BrowserBars />
      {children}
    </NextThemes>
  );
}

/** The address bar and status bar follow the theme as it changes. */
function BrowserBars() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const colour = resolvedTheme === "dark" ? BARS.dark : BARS.light;
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", colour);
  }, [resolvedTheme]);
  return null;
}

type Appearance = "light" | "dark" | "system";

const APPEARANCES: { value: Appearance; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "Device" },
];

/** This device's choice, and what Account's row calls it. */
export function useAppearance() {
  const { theme, setTheme } = useTheme();
  const value: Appearance = theme === "light" || theme === "dark" ? theme : "system";
  const label = APPEARANCES.find((option) => option.value === value)!.label;
  return { value, label, set: setTheme };
}

/** Light | Dark | Device, which takes effect as it is tapped: what changes is the page itself. */
export function AppearanceControl() {
  const { value, set } = useAppearance();
  return (
    <SegmentedControl<Appearance>
      label="Light or dark"
      labelHidden
      fill
      name="appearance"
      value={value}
      onChange={set}
      options={APPEARANCES}
    />
  );
}
